import { useEffect, useRef, useState } from "react";
import { Box, Button, FormControlLabel, Grid, Switch, TextField, Typography } from "@mui/material";
import FormWrapper from "../../../components/FormWrapper";
import useBreadcrumbs from "../../../hooks/useBreadcrumbs";
import useAlert from "../../../hooks/useAlert";
import { useNavigate } from "react-router-dom";
import CustomAutocomplete from "../../../components/Inputs/CustomAutocomplete";
import CustomDateTimePicker from "../../../components/Inputs/CustomDateTimePicker";
import dayjs from "dayjs";
import moment from "moment";
import axios from "../../../services/Api";
import CustomRadioButtons from "../../../components/Inputs/CustomRadioButtons";

const initialValues = {
  eventTitle: "",
  description: "",
  startDate: "",
  endDate: "",
  type: "Faculty",
  contributionType: "",
  taskType: "",
  studentStatus: false,
};

const TaskList = [
  { label: "Primary", value: "Primary" },
  { label: "Secondary", value: "Secondary" },
  { label: "Additional", value: "Additional" },
];

const ContributionType = [
  { label: "Departmental Level", value: "Departmental Level" },
  { label: "Institutional Level", value: "Institutional Level" },
  { label: "University Level", value: "University Level" },
];

const EventForm = () => {
  const setCrumbs = useBreadcrumbs();
  const { setAlertMessage, setAlertOpen } = useAlert();
  const navigate = useNavigate();
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const TOTAL_CHARACTERS = 200;
  const characterRemaining = useRef(200);
  const [loading, setLoading] = useState(false)
  const [taskTypeList, setTaskTypeList] = useState([])

  useEffect(() => {
    setCrumbs([
      { name: "Daily Planner", link: "/daily-planner" },
      { name: "Create" },
    ]);
  }, []);

  const handleChange = (e) => {
    if (e.target.name === "studentStatus") {
      setValues((prev) => ({
        ...prev,
        studentStatus: e.target.checked,
      }));
      return;
    }

    if (e.target.name === "description") {
      if (e.target.value.length > TOTAL_CHARACTERS) return;

      characterRemaining.current = TOTAL_CHARACTERS - e.target.value.length;
    }

    if (e.target.name === "type") {
      setTaskTypeList([]);
      setValues((prev) => ({
        ...prev,
        contributionType: "",
        taskType: "",
      }));
    }

    setValues((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const fetchTaskTypes = async (contributionType) => {
    if (!contributionType) {
      setTaskTypeList([]);
      return;
    }

    try {
      const res = await axios.get(
        `/api/getTaskDetailsBasedOnContributionType/${encodeURIComponent(
          contributionType
        )}`
      );
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setTaskTypeList(
        data.map((item) => ({
          value: item.category_detail,
          label: item.category_detail,
          category_details_id: item.category_details_id,
        }))
      );
    } catch (err) {
      console.error(err);
      setTaskTypeList([]);
      setAlertMessage({
        severity: "error",
        message: "Failed to load task types",
      });
      setAlertOpen(true);
    }
  };

  const handleChangeAdvance = (name, newValue) => {
    if (name === "contributionType") {
      setValues((prev) => ({
        ...prev,
        contributionType: newValue,
        taskType: "",
      }));
      fetchTaskTypes(newValue);
      return;
    }

    setValues((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleError = (payload) => {
    const { eventTitle, startDate, endDate, task, contributionType, taskType } = payload;
    let error = {};

    if (eventTitle === null || eventTitle === "")
      error["eventTitle"] = "Event Title is required";

    if (startDate === null || startDate === "")
      error["startDate"] = "Start Date is required";

    if (endDate === null || endDate === "")
      error["endDate"] = "End Date is Required";

    if (task === null || task === "") error["task"] = "Task is Required";

    if (values.type === "Faculty") {
      if (contributionType === null || contributionType === "") error["contributionType"] = "Contribution Type is Required";

      if (taskType === null || taskType === "") error["taskType"] = "Task Type is Required";
    }

    return { error, isValid: Object.keys(error).length <= 0 };
  };

  const handleCreate = async () => {
    setLoading(true)
    setErrors({});

    const payload = {
      eventTitle: values.eventTitle,
      description: values.description,
      startDate: values.startDate,
      endDate: values.endDate,
      task: values.task,
      contributionType: values.contributionType,
      taskType: values.taskType
    };

    const { error, isValid } = handleError(payload);
    console.log(error, isValid);
    
    if (!isValid) {
      setErrors(error)
      setLoading(false)
      return
    }

    const empId = await getEmpId();
    if (empId === null) {
      setLoading(false)
      alert("No User Found");
      return
    }
    const body = {
      emp_id: empId,
      task_title: values.eventTitle,
      task_priority: values.task,
      description: values.description,
      task_status: "Pending",
      from_date: moment(values.startDate).format("DD-MM-YYYY"),
      to_date: moment(values.endDate).format("DD-MM-YYYY"),
      from_time: moment(values.startDate).format("HH:mm"),
      to_time: moment(values.endDate).format("HH:mm"),
      active: true,
      type: values.type,
      contribution_type: values.contributionType,
      task_type: values.taskType,
      category_details_id:
        taskTypeList.find((opt) => opt.value === values.taskType)
          ?.category_details_id ?? null,
      student_status: values.studentStatus === true,
    };

    axios
      .post("/api/saveDailyPlanner", body)
      .then((res) => {
        navigate("/daily-planner");
      })
      .catch((err) => {
        console.log(err);
        setAlertMessage({
          severity: "error",
          message: "Failed to create, Please try after sometime",
        });
        setAlertOpen(true);
        setLoading(false)
      });
  };

  const getEmpId = async () => {
    return new Promise((resolve) => {
      const userId = JSON.parse(
        sessionStorage.getItem("AcharyaErpUser")
      )?.userId;
      axios
        .get(`/api/employee/getEmployeeDetailsByUserID/${userId}`)
        .then((res) => {
          if (res.data.data !== null) resolve(res.data.data.emp_id);
          else resolve(null);
        })
        .catch((err) => {
          console.error(err);
          resolve(null);
        });
    });
  };

  return (
    <Box p={3}>
      <FormWrapper>
        <Grid
          container
          alignItems="flex-start"
          rowSpacing={3}
          columnSpacing={{ xs: 2, md: 4 }}
        >
          <Grid item xs={12}>
            <CustomRadioButtons
              name="type"
              label="Type"
              value={values.type}
              items={[
                { value: "Faculty", label: "Faculty" },
                { value: "Personal", label: "Personal" },
              ]}
              handleChange={handleChange}
              required
            />
          </Grid>

          {values.type === "Faculty" && (
            <>
              <Grid item xs={12} md={6}>
                <CustomAutocomplete
                  error={errors.contributionType ? true : false}
                  name="contributionType"
                  label="Contribution Type"
                  options={ContributionType}
                  value={values.contributionType}
                  handleChangeAdvance={handleChangeAdvance}
                  required
                />
                {errors.contributionType && (
                  <Typography variant="caption" sx={{ color: "red" }}>
                    {errors.contributionType}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomAutocomplete
                  error={errors.taskType ? true : false}
                  name="taskType"
                  label="Task Type"
                  options={taskTypeList}
                  value={values.taskType}
                  handleChangeAdvance={handleChangeAdvance}
                  disabled={!values.contributionType}
                  required
                />
                {errors.taskType && (
                  <Typography variant="caption" sx={{ color: "red" }}>
                    {errors.taskType}
                  </Typography>
                )}
              </Grid>
            </>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              error={errors.eventTitle ? true : false}
              fullWidth
              size="small"
              label="Task Title"
              name="eventTitle"
              value={values.eventTitle}
              onChange={handleChange}
            />
            {errors.eventTitle && (
              <Typography variant="caption" sx={{ color: "red" }}>
                {errors.eventTitle}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <CustomAutocomplete
              error={errors.task ? true : false}
              name="task"
              label="Task"
              options={TaskList}
              value={values.task}
              handleChangeAdvance={handleChangeAdvance}
              required
            />
            {errors.task && (
              <Typography variant="caption" sx={{ color: "red" }}>
                {errors.task}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <CustomDateTimePicker
              name="startDate"
              label="Start Date"
              value={values.startDate}
              handleChangeAdvance={handleChangeAdvance}
              minDateTime={dayjs(new Date().toString())}
            />
            {errors.startDate && (
              <Typography variant="caption" sx={{ color: "red" }}>
                {errors.startDate}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <CustomDateTimePicker
              name="endDate"
              label="End Date"
              value={values.endDate}
              handleChangeAdvance={handleChangeAdvance}
              minDateTime={dayjs(new Date(values.startDate).toString())}
              disablePast
            />
            {errors.endDate && (
              <Typography variant="caption" sx={{ color: "red" }}>
                {errors.endDate}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(values.studentStatus)}
                  onChange={handleChange}
                  name="studentStatus"
                />
              }
              label="Do you want students to participate?"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              error={errors.description ? true : false}
              fullWidth
              size="small"
              label="Description"
              name="description"
              multiline
              rows={4}
              value={values.description}
              onChange={handleChange}
            />
            {errors.description && (
              <Typography variant="caption" sx={{ color: "red" }}>
                {errors.description}
              </Typography>
            )}
            <Box sx={{ textAlign: "right" }}>
              <Typography>
                {characterRemaining.current} Characters remaining
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} align="right">
            <Button variant="contained" onClick={handleCreate} disabled={loading}>
              <Typography variant="subtitle2">Create</Typography>
            </Button>
          </Grid>
        </Grid>
      </FormWrapper>
    </Box>
  );
};

export default EventForm;
